import { create } from "zustand";
import Communicator from "../Helper/Communicator";
import { useFormStore } from "./Form";

export enum AppMode {
  WIDGET = "widget",
  SURVEY_FULL = "survey_full",
  SURVEY_WEB = "survey_web",
  SURVEY = "survey",
  // Surveys 2.0 survey page / embeds (HelpCenterApp `/s/[slug]`, inline
  // embeds): the survey fills the frame on a transparent background, no close
  // button, height posted to the host.
  SURVEY_PAGE = "survey_page",
  CHATBAR = "chatbar",
}

export enum NavigationDirection {
  FORWARD = "forward",
  BACKWARD = "backward",
}

// The two routes that make up an open conversation: the takeoff loading shell
// and the conversation itself. Several takeoff consumers need to tell "we've
// navigated into the conversation" apart from "still on home" — the store flips
// `aiTakeoffActive` false the moment it pushes the shell, but App renders the
// routed page from a DEFERRED currentPage, so `currentPage` is the reliable
// "have we left home yet" signal during that 1-2 frame lag.
export const isConversationRoute = (page: string | undefined) =>
  page === "conversation" || page === "conversationloading";

interface RouterState {
  appMode: AppMode;
  navigationDirection: NavigationDirection;
  openVideoCall: boolean;
  currentPage: string;
  currentData: any;
  showBackButton: boolean;
  startPageLoading: boolean;
  // Bumped by CommunicationManager on every closed→open transition of the
  // widget. Home + TabBar key off it so their entrance choreography replays
  // when the widget is actually REVEALED — on first boot the app renders
  // behind the SDK's loading placeholder (iframe rendered but invisible), so
  // plain mount-time animations would play unseen.
  widgetOpenCount: number;
  history: { page: string; data: any }[];
  joiningVideoCallId: string | null;
  setJoiningVideoCallId: (joiningVideoCallId: string | null) => void;
  setOpenVideoCall: (openVideoCall: boolean) => void;
  setStartPageLoading: (loading: boolean) => void;
  setAppMode: (appMode: AppMode) => void;
  setPage: (page: string, data?: any, appMode?: AppMode) => void;
  pushPage: (page: string, data?: any) => void;
  popPage: () => void;
  reset: () => void;
}

export const useRouterStore = create<RouterState>()((set, get) => {
  /**
   * Helper function to change the page and update the state.
   * @param {string} page - The new page to navigate to.
   * @param {any} data - The data associated with the new page.
   * @param {AppMode} appMode - The application mode.
   * @param {boolean} resetHistory - Whether to reset the history.
   */
  const changePage = (
    page: string,
    data: any,
    appMode: AppMode,
    resetHistory: boolean
  ) => {
    Communicator.pageChanged(page);

    const navigationDirection =
      get().currentPage === "menu"
        ? NavigationDirection.FORWARD
        : NavigationDirection.BACKWARD;

    const newState: any = {
      currentPage: page,
      currentData: data,
      navigationDirection: navigationDirection,
      appMode,
    };

    if (resetHistory) {
      newState.history = [
        {
          page,
          data,
        },
      ];
    }

    if (page === "flow" && data.flowId) {
      // Set feedbackFlow in store.
      useFormStore.setState({
        feedbackFlow: data.flowId,
        handoffTicketId:
          typeof data.handoffTicketId === "string" && data.handoffTicketId
            ? data.handoffTicketId
            : undefined,
      });
    }

    set(newState);
  };

  return {
    appMode: AppMode.WIDGET,
    openVideoCall: false,
    history: [],
    currentPage: "menu",
    currentData: {},
    showBackButton: true,
    startPageLoading: false,
    widgetOpenCount: 0,
    joiningVideoCallId: null,
    navigationDirection: NavigationDirection.FORWARD,
    /**
     * Sets the current page and data, and resets the history.
     * Use this when you want to start a new navigation flow.
     * @param {string} page - The new page to navigate to.
     * @param {any} data - The data associated with the new page.
     * @param {AppMode} appMode - The application mode.
     */
    setPage: (page: string, data: any, appMode: AppMode = AppMode.WIDGET) => {
      changePage(page, data, appMode, true);
    },
    /**
     * Pushes a new page onto the history stack.
     * Use this when you want to navigate forward and maintain the current history.
     * @param {string} page - The new page to navigate to.
     * @param {any} data - The data associated with the new page.
     */
    pushPage: (page: string, data: any) => {
      const { history } = get();

      // Pages to ignore in history.
      const ignorePages = ["conversationloading"];
      if (page && !ignorePages.includes(page)) {
        set({
          history: [
            ...history,
            {
              page: page,
              data: data,
            },
          ],
        });
      }
      changePage(page, data, get().appMode, false);
    },
    /**
     * Pops the last page from the history stack and navigates back.
     * If the history is empty, logs a warning.
     */
    popPage: () => {
      const history = get().history;
      const currentPage = get().currentPage;
      const historyLength = history.length;

      if (historyLength === 0) {
        changePage("menu", {}, get().appMode, true);
        return;
      }

      const lastHistoryEntry = history[historyLength - 1];
      const currentPageIsLastInHistory = lastHistoryEntry?.page === currentPage;

      if (currentPageIsLastInHistory) {
        // Normal case: current page is the last entry in history.
        if (historyLength <= 1) {
          changePage("menu", {}, get().appMode, true);
          return;
        }
        const targetPage = history[historyLength - 2];
        changePage(targetPage.page, targetPage.data, get().appMode, false);
        set({
          history: history.slice(0, historyLength - 1),
          navigationDirection: NavigationDirection.BACKWARD,
        });
      } else {
        // Current page is NOT in history (ignored page like "conversationloading").
        // Navigate to the last history entry without modifying the stack.
        changePage(
          lastHistoryEntry.page,
          lastHistoryEntry.data,
          get().appMode,
          false
        );
        set({
          navigationDirection: NavigationDirection.BACKWARD,
        });
      }
    },
    setStartPageLoading: (loading: boolean) => {
      set({ startPageLoading: loading });
    },
    setAppMode: (appMode: AppMode) => {
      set({ appMode });
    },
    setJoiningVideoCallId: (joiningVideoCallId: string | null) => {
      set({
        joiningVideoCallId,
      });
    },
    setOpenVideoCall: (openVideoCall: boolean) => {
      set({
        joiningVideoCallId: null,
        openVideoCall,
      });
    },
    reset: () => {
      set({
        history: [],
        currentPage: "menu",
        currentData: {},
      });
    },
  };
});
